import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { Button, TextButton } from '@creditornot/alchemy-react';
import { Modal } from '@creditornot/cbt-modal';
import { FileInput } from 'common/components/cookbook/FileInput';
import { ModalFooter } from 'common/components/cookbook/Modals/ModalFooter';
import { Stack } from 'common/components/cookbook/Stack';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useSelectedCountryCodes } from 'common/hooks/useSelectedCountryCodes';
import { useToast } from 'common/hooks/useToast';
import { useApiQueryClient } from 'common/query/useApiQueryClient';
import { downloadBlob } from 'common/utils/downloadUtils';
import {
  bulkUploadDeleteOverridesCsv,
  downloadDeleteOverridesExample,
} from 'features/override-management/api';
import { BULK_OPERATIONS_QUERY_KEY_ROOT } from 'features/override-management/query';

import { DeleteOverridesFileFormatInfo } from './DeleteOverridesFileFormatInfo';

type Props = {
  isOpen: boolean;
  onDismiss: () => void;
};

export const DeleteOverridesModal = ({ isOpen, onDismiss }: Props) => {
  const format = useFormatMessage();
  const [countryCode] = useSelectedCountryCodes();
  const [files, setFiles] = useState<File[]>([]);
  const queryClient = useApiQueryClient();
  const { addErrorToast, addSuccessToast } = useToast();

  const { mutate: downloadExample } = useMutation({
    mutationFn: downloadDeleteOverridesExample,
    onSuccess: (blob) => {
      const fileName = 'delete_overrides_example.csv';
      downloadBlob(blob, fileName);
    },
  });

  const handleDismiss = () => {
    setFiles([]);
    onDismiss();
  };

  const { mutate: uploadDeleteOverrides, isPending: deleteOverridesUploadPending } = useMutation({
    mutationFn: (file: File) => bulkUploadDeleteOverridesCsv(countryCode, file),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [BULK_OPERATIONS_QUERY_KEY_ROOT, countryCode],
      });
      setFiles([]);
      onDismiss();
      addSuccessToast({ description: format('forecasting.file_uploaded_for_processing') });
    },
    onError: addErrorToast,
  });

  return (
    <Modal
      sizePreset="centered:large"
      open={isOpen}
      onDismiss={handleDismiss}
      title={format('forecasting.upload_delete_overrides')}
      FooterComponent={ModalFooter}
      footer={
        <Stack
          direction="row"
          gap="regularMedium"
          alignItems="center"
          justifyContent="flex-end"
          style={{ width: '100%' }}
        >
          <Button variant="secondaryNeutral" onClick={handleDismiss}>
            {format('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => uploadDeleteOverrides(files[0])}
            loading={deleteOverridesUploadPending}
            disabled={deleteOverridesUploadPending || files.length === 0}
          >
            {format('common.upload')}
          </Button>
        </Stack>
      }
    >
      <Stack style={{ width: '100%' }} gap="regularLarge">
        <TextButton onClick={() => downloadExample()}>
          {format('common.download_example_file')}
        </TextButton>
        <DeleteOverridesFileFormatInfo />
        <FileInput
          onDrop={setFiles}
          id="deleteOverridesCsv"
          accept={{ 'text/csv': ['.csv'] }}
          selectedFiles={files.map((file) => file.name)}
          buttonText={format('common.choose_csv_file')}
        />
      </Stack>
    </Modal>
  );
};
